// A sauna's slatted floor grille (the Floor grille tool): top slats laid side
// by side over battens across them, over the whole floor — 3 mm short of the
// walls and partitions, 10 mm in under a skirted bench's skirting (no floor
// is needed behind it), carved round an unskirted bench's trestles, a heater's
// cover (its air gap where it has none) and every part standing on the floor,
// 3 mm clear of each. The top slats run along the wall of the room's first
// door, full from the door's side, the last one ripped to fit. Pure and
// DOM-free (test/floorGrille.test.mjs; docs/claude/floorGrille.md).

import type {
  ProductInstance,
  Vec2,
} from '../shared/types';
import { traitsOf } from '../shared/categories';
import {
  type Wood,
  type WoodId,
  woodOf,
} from '../shared/woods';
import {
  benchSupportMarks,
  roomBenchLines,
} from './benchSolids';
import { floorOccupants } from './floorOccupancy';
import {
  BAND_MITRE,
  offsetPolyline,
  pointInPolygon,
  toInstanceLocal,
} from './geometry';
import {
  polygonBoolean,
  type RingPoly,
} from './polybool';
import {
  isPolyRoom,
  polyEdges,
  polyInnerCarved,
  polyInnerWallFill,
  toCCW,
} from './polyRoom';
import { wallThicknessOf } from './dent';
import {
  type GrilleFigures,
  grilleFigures,
  grilleTop,
} from './grilleFigures';

export {
  GRILLE_DEFAULTS,
  type GrilleFigures,
  grilleFigures,
  grilleTop,
} from './grilleFigures';

/** How far short of a wall, a partition or anything it is carved round a
 *  grille stops (mm). */
export const GRILLE_CLEAR = 3;
/** How far in under a skirted bench's skirting a grille runs (mm): the
 *  skirting stands on it. */
export const GRILLE_UNDER_SKIRTING = 10;
/** A ripped slat or batten narrower than this is left out (mm). */
export const GRILLE_MIN_PIECE = 10;

/** A grille's wood when the room's benches have no colour of their own. */
export const GRILLE_COLOR = '#d9b98a';

/** The wood a room's grille is made of: its own, else its benches'; null
 *  when neither has one. */
export function grilleWood(room: Pick<ProductInstance, 'floorGrille' | 'woods'>): (Wood & { id: WoodId }) | null {
  return woodOf(room.floorGrille?.wood) ?? woodOf(room.woods?.benches?.wood);
}

/** The colour a room's grille is drawn in (the plan, a section, 3D before its
 *  wood's image loads): its wood's, else the benches' colour. */
export function grilleColor(room: Pick<ProductInstance, 'benchColor' | 'floorGrille' | 'woods'>): string {
  return grilleWood(room)?.color ?? room.benchColor ?? GRILLE_COLOR;
}

/** Whether a room can take a grille: a sauna, polygonal. */
export function takesGrille(room: ProductInstance): boolean {
  return room.category === 'sauna' && !!room.autoBenches && isPolyRoom(room);
}

/** A closed ring grown by `d` (mm) all round. */
function grown(ring: readonly Vec2[], d: number): Vec2[] {
  return offsetPolyline(toCCW([...ring]), -d, true, BAND_MITRE);
}

/** The room's first door (on an outer wall), read without migrating a legacy
 *  `door`. */
function firstDoor(room: ProductInstance): { edge: number; offset: number } | null {
  const d = room.doors?.find((x) => x.innerWallId == null) ?? (room.door && room.door.innerWallId == null ? room.door : null);
  return d ? { edge: d.edge ?? 0, offset: d.offset || 0 } : null;
}

/** Where a grille's slats are laid from (room local): `dir` the way the top
 *  slats run — along the first door's wall, else the longest —, `door` a point
 *  just inside the door (none without one). */
export function grilleAxes(room: ProductInstance, turned = !!room.floorGrille?.turned): { dir: Vec2; door: Vec2 | null } {
  const edges = polyEdges(room);
  const fd = firstDoor(room);
  const e = (fd && edges[fd.edge]) || edges.reduce((m, x) => (!m || x.len > m.len ? x : m), edges[0]);
  if (!e) return { dir: { x: 1, y: 0 }, door: null };
  const dir = turned ? { x: -e.dir.y, y: e.dir.x } : e.dir;
  let door: Vec2 | null = null;
  if (fd && edges[fd.edge]) {
    const s = e.len / 2 + fd.offset, d = wallThicknessOf(room) + 50;
    door = { x: e.a.x + e.dir.x * s + e.normal.x * d, y: e.a.y + e.dir.y * s + e.normal.y * d };
  }
  return { dir, door };
}

/** The top of the grille a standalone sauna bench stands on (mm over its
 *  floor): its room's, the room under the middle of its node line; 0 where
 *  there is none. */
export function grilleUnder(instances: readonly ProductInstance[], bench: ProductInstance): number {
  const pts = bench.nodes?.length ? bench.nodes : [{ x: bench.x, y: bench.y }];
  const c = pts.reduce((m, q) => ({ x: m.x + q.x / pts.length, y: m.y + q.y / pts.length }), { x: 0, y: 0 });
  const room = instances.find((r) => r.floorGrille && r !== bench && r.polygon && r.polygon.length >= 3 && pointInPolygon(toInstanceLocal(r, c), r.polygon));
  return room ? grilleTop(room) : 0;
}

/** What stands on a room's floor and carves its grille, each footprint in the
 *  room's frame (ungrown), from among `parts` (the drawing's instances;
 *  floorOccupancy.ts finds those standing in it): a heater's cover — its air
 *  gap where it has none —, a standalone sauna bench (to 10 mm in from its
 *  skirting's face, or its trestles where it has no skirting), and any other
 *  part reaching below the grille's top. What `settleFloorGrilles` stores as
 *  `floorGrilleCuts`. */
export function grilleCutsOf(room: ProductInstance, parts: readonly ProductInstance[]): Vec2[][] {
  if (!room.floorGrille) return [];
  const top = grilleTop(room);
  const out: Vec2[][] = [];
  for (const o of floorOccupants(room, parts)) {
    if (o.bench) {
      const skirt = o.bench.skirtFront;
      if (skirt != null) out.push(o.bench.ribbon(Math.max(1, skirt - GRILLE_UNDER_SKIRTING)));
      else out.push(...o.bench.trestles());
    } else if (traitsOf(o.part.category).guarded) out.push(o.outline);
    else if (o.z0 < top - 1e-6 && o.foot.length >= 3) out.push(o.foot);
  }
  return out;
}

/** The room with its grille's cuts as what of `parts` stands on its floor now
 *  (`grilleCutsOf`): what the tool's hover lays, and what a settled room
 *  holds. */
export function withGrilleCuts(room: ProductInstance, parts: readonly ProductInstance[]): ProductInstance {
  const cuts = grilleCutsOf(room, parts);
  const out: ProductInstance = { ...room };
  if (cuts.length) out.floorGrilleCuts = cuts;
  else delete out.floorGrilleCuts;
  return out;
}

/** THE writer of `floorGrilleCuts`: every grilled room among `instances`
 *  learns what stands on its floor (a heater, a bench, a part moved, added or
 *  taken away); a room without a grille loses any. Written only where they
 *  changed, so a room's version moves only then. The fixtures' reconciling
 *  (ui/roomFixtures.ts) calls it inside each edit's undo step. */
export function settleFloorGrilles(instances: ProductInstance[]): void {
  for (const room of instances) {
    if (!room.floorGrille && !room.floorGrilleCuts) continue;
    const cuts = grilleCutsOf(room, instances);
    if (!cuts.length) delete room.floorGrilleCuts;
    else if (JSON.stringify(cuts) !== JSON.stringify(room.floorGrilleCuts ?? null)) room.floorGrilleCuts = cuts;
  }
}

/** What a room's own build takes out of its grille (room local, each grown
 *  by what it keeps clear): its partitions, a skirted bench to 10 mm in under
 *  its skirting, an unskirted bench's trestles. */
function roomCuts(room: ProductInstance): Vec2[][] {
  const out: Vec2[][] = [];
  for (const iw of room.innerWalls ?? []) for (const q of polyInnerWallFill(room, iw)) if (q.length >= 3) out.push(grown(q, GRILLE_CLEAR));
  const lines = roomBenchLines(room);
  const skirted = new Set<string>();
  for (const l of lines) {
    if (!l.skirting || l.skirting.base > 1) continue;
    skirted.add(l.id);
    if (l.sourceId) skirted.add(l.sourceId);
    const d = Math.max(1, l.skirting.d - GRILLE_UNDER_SKIRTING);
    const ring = [...l.path(0), ...l.path(d).reverse()];
    if (ring.length >= 3) out.push(ring);
  }
  for (const m of benchSupportMarks(room)) if (!skirted.has(m.bench)) out.push(grown(m.poly, GRILLE_CLEAR));
  return out;
}

/** Axis-aligned bounds. */
function bounds(pts: readonly Vec2[]): { x0: number; y0: number; x1: number; y1: number } {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const p of pts) { x0 = Math.min(x0, p.x); y0 = Math.min(y0, p.y); x1 = Math.max(x1, p.x); y1 = Math.max(y1, p.y); }
  return { x0, y0, x1, y1 };
}

const overlaps = (a: ReturnType<typeof bounds>, b: ReturnType<typeof bounds>): boolean => a.x0 < b.x1 && b.x0 < a.x1 && a.y0 < b.y1 && b.y0 < a.y1;

/** The plan of a room's grille (room local): its floor 3 mm short of the
 *  walls, less what stands on it — only the part reached from the first door
 *  (where it has one), so a strip shut in behind a skirting is left bare. */
export function grilleRegion(room: ProductInstance): RingPoly[] {
  if (!room.floorGrille || !isPolyRoom(room)) return [];
  const inner = polyInnerCarved(room);
  if (inner.length < 3) return [];
  let rings: RingPoly[] = [{ outer: offsetPolyline(toCCW(inner), GRILLE_CLEAR, true, BAND_MITRE), holes: [] }];
  const cuts = [...roomCuts(room), ...(room.floorGrilleCuts ?? []).map((c) => grown(c, GRILLE_CLEAR))];
  for (const cut of cuts) {
    if (cut.length < 3) continue;
    const cb = bounds(cut);
    rings = rings.flatMap((r) => (overlaps(bounds(r.outer), cb) ? polygonBoolean(r, { outer: cut, holes: [] }, 'difference') : [r]));
    if (!rings.length) return [];
  }
  const { door } = grilleAxes(room);
  if (door && rings.length > 1) {
    const inside = (r: RingPoly): boolean => pointInPolygon(door, r.outer) && !r.holes.some((h) => pointInPolygon(door, h));
    const at = rings.find(inside) ?? nearest(rings, door);
    rings = at ? [at] : rings;
  }
  return rings;
}

/** The ring whose outline comes nearest a point. */
function nearest(rings: RingPoly[], p: Vec2): RingPoly | null {
  let best: RingPoly | null = null, bd = Infinity;
  for (const r of rings) {
    const n = r.outer.length;
    for (let i = 0; i < n; i++) {
      const a = r.outer[i], b = r.outer[(i + 1) % n];
      const dx = b.x - a.x, dy = b.y - a.y, L = dx * dx + dy * dy || 1;
      const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / L));
      const d = Math.hypot(a.x + dx * t - p.x, a.y + dy * t - p.y);
      if (d < bd) { bd = d; best = r; }
    }
  }
  return best;
}

/** One slat or batten of a grille (room local): its plan (carved, maybe with
 *  holes), its height band over the floor, its length along itself and width
 *  across. */
export interface GrillePiece {
  kind: 'slat' | 'batten';
  ring: RingPoly;
  z0: number;
  z1: number;
  length: number;
  width: number;
}

/** A room's grille, slat by slat. */
export interface GrilleLayout {
  /** The way the top slats run (room local). */
  dir: Vec2;
  region: RingPoly[];
  slats: GrillePiece[];
  battens: GrillePiece[];
  /** Its top over the floor (mm). */
  top: number;
}

const LAYOUTS = new Map<string, GrilleLayout>();

/** A room's grille as built: the top slats `slatWidth` wide, `slatGap` apart,
 *  stacked from the door's side of its floor (the last ripped to fit; one
 *  under `GRILLE_MIN_PIECE` left out), over battens running across them, one
 *  flush with each end of the floor and the rest evenly between, at most
 *  `battenSpacing` apart centre to centre; each clipped to the floor's plan.
 *  Null without a grille. */
export function grilleLayout(room: ProductInstance): GrilleLayout | null {
  if (!room.floorGrille || !isPolyRoom(room)) return null;
  const key = JSON.stringify(room);
  const hit = LAYOUTS.get(key);
  if (hit) return hit;
  const f = grilleFigures(room.floorGrille);
  const { dir, door } = grilleAxes(room);
  const across = { x: -dir.y, y: dir.x };
  const region = grilleRegion(room);
  const U = (p: Vec2): number => p.x * dir.x + p.y * dir.y, V = (p: Vec2): number => p.x * across.x + p.y * across.y;
  const at = (u: number, v: number): Vec2 => ({ x: dir.x * u + across.x * v, y: dir.y * u + across.y * v });
  const all = region.flatMap((r) => r.outer);
  const out: GrilleLayout = { dir, region, slats: [], battens: [], top: f.battenThickness + f.slatThickness };
  if (all.length >= 3) {
    const u0 = Math.min(...all.map(U)) - 1, u1 = Math.max(...all.map(U)) + 1;
    const v0 = Math.min(...all.map(V)), v1 = Math.max(...all.map(V));
    const strip = (a0: number, a1: number, b0: number, b1: number, alongU: boolean): Vec2[] => alongU
      ? [at(a0, b0), at(a1, b0), at(a1, b1), at(a0, b1)]
      : [at(b0, a0), at(b0, a1), at(b1, a1), at(b1, a0)];
    const clip = (kind: GrillePiece['kind'], ring: Vec2[], z0: number, z1: number, along: (p: Vec2) => number, wide: (p: Vec2) => number): void => {
      const rb = bounds(ring);
      for (const r of region) {
        if (!overlaps(bounds(r.outer), rb)) continue;
        for (const piece of polygonBoolean(r, { outer: ring, holes: [] }, 'intersection')) {
          const ls = piece.outer.map(along), ws = piece.outer.map(wide);
          const width = Math.max(...ws) - Math.min(...ws), length = Math.max(...ls) - Math.min(...ls);
          if (width >= GRILLE_MIN_PIECE - 1e-6 && length >= GRILLE_MIN_PIECE - 1e-6) out[kind === 'slat' ? 'slats' : 'battens'].push({ kind, ring: piece, z0, z1, length, width });
        }
      }
    };
    // The top slats, full from the door's side.
    const fromHi = door != null && Math.abs(V(door) - v1) < Math.abs(V(door) - v0);
    const pitch = f.slatWidth + f.slatGap;
    const n = Math.ceil((v1 - v0 + f.slatGap) / pitch);
    const zs0 = f.battenThickness, zs1 = zs0 + f.slatThickness;
    for (let k = 0; k < n; k++) {
      const a = fromHi ? v1 - k * pitch - f.slatWidth : v0 + k * pitch;
      const b0 = Math.max(v0, a), b1 = Math.min(v1, a + f.slatWidth);
      if (b1 - b0 > 0) clip('slat', strip(u0, u1, b0, b1, true), zs0, zs1, U, V);
    }
    // The battens across them: one flush with each end, the rest evenly between.
    const span = u1 - u0 - 2 - f.battenWidth;
    const gaps = Math.max(1, Math.ceil(span / f.battenSpacing - 1e-9));
    for (let k = 0; k <= gaps; k++) {
      const c = u0 + 1 + f.battenWidth / 2 + (span > 0 ? (span * k) / gaps : 0);
      clip('batten', strip(v0 - 1, v1 + 1, c - f.battenWidth / 2, c + f.battenWidth / 2, false), 0, f.battenThickness, V, U);
      if (span <= 0) break;
    }
  }
  if (LAYOUTS.size > 16) LAYOUTS.clear();
  LAYOUTS.set(key, out);
  return out;
}

/** One line of a grille's cut list: how many pieces of one size. */
export interface GrilleCutRow {
  kind: GrillePiece['kind'];
  count: number;
  /** Rounded up to the whole mm. */
  length: number;
  width: number;
  thickness: number;
}

/** A grille's cut list: its slats then its battens, each size once, the
 *  longest first; a carved piece listed by its full length and width. */
export function grilleCutList(layout: GrilleLayout, figures: GrilleFigures): GrilleCutRow[] {
  const rows = new Map<string, GrilleCutRow>();
  for (const p of [...layout.slats, ...layout.battens]) {
    const length = Math.ceil(p.length - 1e-6), width = Math.round(p.width);
    const thickness = p.kind === 'slat' ? figures.slatThickness : figures.battenThickness;
    const key = `${p.kind}|${length}|${width}`;
    const row = rows.get(key);
    if (row) row.count++;
    else rows.set(key, { kind: p.kind, count: 1, length, width, thickness });
  }
  return [...rows.values()].sort((a, b) => (a.kind === b.kind ? b.length - a.length || b.width - a.width : a.kind === 'slat' ? -1 : 1));
}
